/**
 * Interpretación del resultado de envío de documentos (cliente).
 * No afirma entrega al buzón; solo aceptación SMTP cuando el backend lo confirma.
 *
 * Fingerprint canónico: lo calcula el backend (fuente de verdad).
 * El cliente solo envía client_operation_id estable por intento lógico.
 */

export const SEND_STATUS = {
  REGISTERED: 'registered',
  SMTP_ACCEPTED: 'smtp_accepted',
  SMTP_PARTIAL: 'smtp_partial',
  SMTP_FAILED: 'smtp_failed',
  REJECTED: 'rejected',
  UNKNOWN: 'unknown',
}

export const SEND_USER_MESSAGES = {
  [SEND_STATUS.SMTP_ACCEPTED]:
    'Documento registrado correctamente. El servidor de correo aceptó las notificaciones correspondientes.',
  [SEND_STATUS.SMTP_PARTIAL]:
    'El documento quedó registrado, pero algunas notificaciones no pudieron enviarse.',
  [SEND_STATUS.SMTP_FAILED]:
    'El documento quedó registrado, pero no fue posible enviar las notificaciones.',
  [SEND_STATUS.REGISTERED]:
    'Documento registrado correctamente. Las notificaciones de esta etapa quedaron pendientes.',
  [SEND_STATUS.REJECTED]:
    'No fue posible registrar el documento. Puedes intentarlo nuevamente.',
  [SEND_STATUS.UNKNOWN]:
    'No pudimos confirmar si el documento quedó registrado. No vuelvas a enviarlo hasta verificar su estado.',
  session_expired:
    'Tu sesión ha vencido. Vuelve a iniciar sesión para continuar.',
}

/**
 * ¿Resetear estado de operación de envío del cliente tras éxito total?
 * (Nombre histórico; ya no hay borradores locales del editor.)
 * Solo con SMTP totalmente aceptado en esta etapa.
 */
export function shouldClearEditorDraft(operationStatus) {
  return operationStatus === SEND_STATUS.SMTP_ACCEPTED
}

/** ¿Abrir modal de enlaces? Solo si el documento quedó registrado y hay firmantes/tokens. */
export function shouldOpenShareModal(operationStatus, firmantes = []) {
  const registeredStatuses = [
    SEND_STATUS.REGISTERED,
    SEND_STATUS.SMTP_ACCEPTED,
    SEND_STATUS.SMTP_PARTIAL,
    SEND_STATUS.SMTP_FAILED,
  ]
  return registeredStatuses.includes(operationStatus) && Array.isArray(firmantes) && firmantes.length > 0
}

/** ¿Bloquear reintento inmediato de Enviar (crear otro documento)? */
export function shouldBlockResend(operationStatus) {
  return (
    operationStatus === SEND_STATUS.UNKNOWN ||
    operationStatus === SEND_STATUS.SMTP_PARTIAL ||
    operationStatus === SEND_STATUS.SMTP_FAILED ||
    operationStatus === SEND_STATUS.REGISTERED
  )
}

/** Tras registro, no generar un UUID nuevo: reutilizar el de la operación. */
export function shouldReuseClientOperationId(operationStatus) {
  return shouldBlockResend(operationStatus) || operationStatus === SEND_STATUS.SMTP_ACCEPTED
}

export function createClientOperationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `op_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Tras timeout/unknown: consultar GET /send-operations/{id} (solo owner en backend).
 */
export async function fetchSendOperationStatus({
  baseUrl,
  clientOperationId,
  token,
  fetchImpl = fetch,
}) {
  const url = `${baseUrl}/api/pdf/send-operations/${encodeURIComponent(clientOperationId)}`
  const response = await fetchImpl(url, {
    method: 'GET',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  })
  if (response.status === 401) {
    return classifyFetchFailure({ status: 401 })
  }
  if (response.status === 403 || response.status === 404) {
    return {
      ok: false,
      operationStatus: SEND_STATUS.REJECTED,
      authExpired: false,
      httpStatus: response.status,
      message: userMessageForStatus(SEND_STATUS.REJECTED),
      firmantes: [],
    }
  }
  if (!response.ok) {
    return {
      ok: false,
      operationStatus: SEND_STATUS.UNKNOWN,
      message: userMessageForStatus(SEND_STATUS.UNKNOWN),
      firmantes: [],
    }
  }
  const payload = await response.json()
  if (payload?.result) {
    const classified = classifySuccessPayload(payload.result)
    return {
      ...classified,
      authExpired: false,
      message: userMessageForStatus(classified.operationStatus),
      clientOperationId,
      fromGet: true,
    }
  }
  if (payload?.document_registered && payload?.documento_id) {
    const classified = classifySuccessPayload({
      success: true,
      documento_id: payload.documento_id,
      operation_status: payload.operation_status || 'registered',
      smtp_expected: payload.smtp_expected,
      smtp_accepted_count: payload.smtp_accepted_count,
      firmantes: [],
    })
    return {
      ...classified,
      authExpired: false,
      message: userMessageForStatus(classified.operationStatus),
      clientOperationId,
      fromGet: true,
    }
  }
  return {
    ok: false,
    operationStatus: SEND_STATUS.UNKNOWN,
    message: userMessageForStatus(SEND_STATUS.UNKNOWN),
    clientOperationId,
    fromGet: true,
    firmantes: [],
  }
}

export function toastKindForStatus(operationStatus) {
  if (operationStatus === SEND_STATUS.SMTP_ACCEPTED || operationStatus === SEND_STATUS.REGISTERED) {
    return 'success'
  }
  if (
    operationStatus === SEND_STATUS.SMTP_PARTIAL ||
    operationStatus === SEND_STATUS.SMTP_FAILED ||
    operationStatus === SEND_STATUS.UNKNOWN
  ) {
    return 'warning'
  }
  return 'error'
}

export function userMessageForStatus(operationStatus, { authExpired = false } = {}) {
  if (authExpired || operationStatus === 'session_expired') {
    return SEND_USER_MESSAGES.session_expired
  }
  return SEND_USER_MESSAGES[operationStatus] || SEND_USER_MESSAGES[SEND_STATUS.REJECTED]
}

/**
 * Clasifica un cuerpo 2xx del backend (contrato aditivo operation_status).
 * Fallback compatible si el campo aún no existe.
 */
export function classifySuccessPayload(payload = {}) {
  if (payload && typeof payload.operation_status === 'string') {
    const status = payload.operation_status
    if (Object.values(SEND_STATUS).includes(status) && status !== SEND_STATUS.REJECTED && status !== SEND_STATUS.UNKNOWN) {
      return {
        ok: true,
        operationStatus: status,
        documentRegistered: payload.document_registered !== false,
        documentoId: payload.documento_id ?? null,
        firmantes: payload.firmantes || [],
        visualizadores: payload.visualizadores || [],
        smtpExpected: payload.smtp_expected ?? null,
        smtpAcceptedCount: payload.smtp_accepted_count ?? payload.notificaciones_enviadas ?? null,
        raw: payload,
      }
    }
  }

  // Compatibilidad: success true + documento_id implica registro confirmado
  if (payload?.success === true && payload?.documento_id != null) {
    const expected = Number(payload.smtp_expected)
    const accepted = Number(payload.smtp_accepted_count ?? payload.notificaciones_enviadas ?? 0)
    let status = SEND_STATUS.REGISTERED
    if (Number.isFinite(expected) && expected > 0) {
      if (accepted <= 0) status = SEND_STATUS.SMTP_FAILED
      else if (accepted >= expected) status = SEND_STATUS.SMTP_ACCEPTED
      else status = SEND_STATUS.SMTP_PARTIAL
    } else if ((payload.notificaciones_enviadas || 0) > 0) {
      status = SEND_STATUS.SMTP_ACCEPTED
    }
    return {
      ok: true,
      operationStatus: status,
      documentRegistered: true,
      documentoId: payload.documento_id,
      firmantes: payload.firmantes || [],
      visualizadores: payload.visualizadores || [],
      smtpExpected: Number.isFinite(expected) ? expected : null,
      smtpAcceptedCount: accepted,
      raw: payload,
    }
  }

  return {
    ok: false,
    operationStatus: SEND_STATUS.UNKNOWN,
    documentRegistered: false,
    documentoId: null,
    firmantes: [],
    visualizadores: [],
    smtpExpected: null,
    smtpAcceptedCount: null,
    raw: payload,
  }
}

/**
 * Clasifica fallos de fetch/HTTP sin afirmar registro ni rollback.
 */
export function classifyFetchFailure(errorOrResponse, { timedOut = false, networkError = false } = {}) {
  if (timedOut || errorOrResponse?.name === 'AbortError' || errorOrResponse?.code === 'TIMEOUT') {
    return {
      ok: false,
      operationStatus: SEND_STATUS.UNKNOWN,
      authExpired: false,
      httpStatus: null,
      message: userMessageForStatus(SEND_STATUS.UNKNOWN),
    }
  }
  if (networkError || errorOrResponse?.code === 'NETWORK') {
    return {
      ok: false,
      operationStatus: SEND_STATUS.UNKNOWN,
      authExpired: false,
      httpStatus: null,
      message: userMessageForStatus(SEND_STATUS.UNKNOWN),
    }
  }

  const status = errorOrResponse?.status ?? errorOrResponse?.httpStatus ?? null
  if (status === 401) {
    return {
      ok: false,
      operationStatus: SEND_STATUS.REJECTED,
      authExpired: true,
      httpStatus: 401,
      message: userMessageForStatus(SEND_STATUS.REJECTED, { authExpired: true }),
    }
  }

  // 503 de esquema/idempotencia: no es éxito, no asume registro
  if (status === 503) {
    return {
      ok: false,
      operationStatus: SEND_STATUS.REJECTED,
      authExpired: false,
      httpStatus: 503,
      serviceUnavailable: true,
      message:
        'El servicio de envío idempotente no está disponible. No se registró el documento. No cierre el editor y reintente más tarde con el mismo identificador.',
    }
  }

  // Errores HTTP claros antes de un cuerpo de registro → rejected (no unknown)
  if (status && status >= 400) {
    return {
      ok: false,
      operationStatus: SEND_STATUS.REJECTED,
      authExpired: false,
      httpStatus: status,
      message: userMessageForStatus(SEND_STATUS.REJECTED),
    }
  }

  return {
    ok: false,
    operationStatus: SEND_STATUS.UNKNOWN,
    authExpired: false,
    httpStatus: status,
    message: userMessageForStatus(SEND_STATUS.UNKNOWN),
  }
}

/**
 * Ejecuta POST de envío con timeout y clasificación uniforme.
 * No envía correos ni escribe DB: solo orquesta el fetch del cliente.
 */
export async function postSendNotifications({
  url,
  token,
  body,
  fetchImpl = fetch,
  timeoutMs = 60000,
}) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  let timer = null
  let timedOut = false

  const timeoutPromise = timeoutMs > 0
    ? new Promise((_, reject) => {
      timer = setTimeout(() => {
        timedOut = true
        try { controller?.abort() } catch { /* ignore */ }
        const err = new Error('TIMEOUT')
        err.name = 'AbortError'
        err.code = 'TIMEOUT'
        reject(err)
      }, timeoutMs)
    })
    : null

  try {
    const fetchPromise = fetchImpl(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify(body),
      signal: controller?.signal,
    })

    const response = timeoutPromise
      ? await Promise.race([fetchPromise, timeoutPromise])
      : await fetchPromise

    if (!response.ok) {
      let detail = null
      try {
        const errBody = await response.json()
        detail = errBody?.detail || null
      } catch { /* ignore */ }
      const failure = classifyFetchFailure({ status: response.status })
      return {
        ...failure,
        detail,
        firmantes: [],
      }
    }

    let payload
    try {
      payload = await response.json()
    } catch {
      return {
        ok: false,
        operationStatus: SEND_STATUS.UNKNOWN,
        authExpired: false,
        message: userMessageForStatus(SEND_STATUS.UNKNOWN),
        firmantes: [],
      }
    }

    const classified = classifySuccessPayload(payload)
    return {
      ...classified,
      authExpired: false,
      message: userMessageForStatus(classified.operationStatus),
    }
  } catch (err) {
    if (timedOut || err?.name === 'AbortError' || err?.code === 'TIMEOUT') {
      return {
        ...classifyFetchFailure(err, { timedOut: true }),
        firmantes: [],
      }
    }
    const networkError = err instanceof TypeError || /failed to fetch|network/i.test(String(err?.message || ''))
    return {
      ...classifyFetchFailure(err, { networkError: !!networkError }),
      firmantes: [],
    }
  } finally {
    if (timer) clearTimeout(timer)
  }
}
